import {type FillBlank, type Slide} from '#/types/capsule'

/**
 * Shared answer formatting used by the capsule CompletionScreen and the
 * FlashBook learner report. Extracted from CompletionScreen.tsx.
 */

export function formatUserAnswer(slide: Slide, userAnswer: unknown): string {
  if (userAnswer == null) return '—'
  try {
    switch (slide.type) {
      case 'single_choice':
      case 'image_choice': {
        const opt = slide.options?.find(o => o.id === userAnswer)
        return opt?.label ?? String(userAnswer)
      }
      case 'true_false':
        return userAnswer === true || userAnswer === 'true' ? 'True' : 'False'
      case 'multiple_choice': {
        const ids = Array.isArray(userAnswer) ? userAnswer : []
        const labels = ids.map(
          id => slide.options?.find(o => o.id === id)?.label ?? id,
        )
        return labels.join(', ') || '—'
      }
      case 'ordering': {
        const ids = Array.isArray(userAnswer) ? (userAnswer as string[]) : []
        return (
          ids
            .map(id => slide.orderItems?.find(i => i.id === id)?.label ?? id)
            .join(' → ') || '—'
        )
      }
      case 'drag_words':
      case 'fill_blanks':
      case 'complex_fill_blank': {
        const vals = Array.isArray(userAnswer)
          ? (userAnswer as (string | null)[])
          : []
        return vals.filter(Boolean).join(', ') || '—'
      }
      case 'mark_words': {
        // Play widgets may report selected words directly, or indices.
        const arr = Array.isArray(userAnswer) ? (userAnswer as unknown[]) : []
        if (arr.every(v => typeof v === 'number')) {
          const wordTokens = (slide.markWordsText ?? '')
            .split(/\s+/)
            .filter(Boolean)
          return (
            (arr as number[])
              .map(i => wordTokens[i])
              .filter(Boolean)
              .join(', ') || '—'
          )
        }
        return arr.join(', ') || '—'
      }
      case 'find_words': {
        const words = Array.isArray(userAnswer) ? (userAnswer as string[]) : []
        return words.join(', ') || '—'
      }
      case 'matching': {
        if (typeof userAnswer !== 'object' || !userAnswer) return '—'
        const matched = userAnswer as Record<string, string>
        return (
          Object.entries(matched)
            .map(([leftId, rightId]) => {
              const leftLabel =
                slide.pairs?.find(p => p.id === leftId)?.left ?? leftId
              const rightLabel =
                slide.pairs?.find(p => p.id === rightId)?.right ?? rightId
              return `${leftLabel} → ${rightLabel}`
            })
            .join('; ') || '—'
        )
      }
      default:
        return '—'
    }
  } catch {
    return '—'
  }
}

export function formatCorrectAnswer(slide: Slide): string {
  try {
    switch (slide.type) {
      case 'single_choice':
      case 'image_choice': {
        const opt = slide.options?.find(o => o.isCorrect)
        return opt?.label ?? '—'
      }
      case 'true_false':
        return slide.answer === 'true' ? 'True' : 'False'
      case 'multiple_choice':
        return (
          slide.options
            ?.filter(o => o.isCorrect)
            .map(o => o.label)
            .join(', ') || '—'
        )
      case 'ordering':
        return slide.orderItems?.map(i => i.label).join(' → ') || '—'
      case 'drag_words': {
        const matches = [
          ...(slide.dragWordsTemplate ?? '').matchAll(/\[([^\]]+)\]/g),
        ]
        return matches.map(m => m[1]).join(', ') || '—'
      }
      /*
       * The plural `fill_blanks` block keeps its answers beside its paragraph, not
       * in `blanks` — that array belongs to the singular `fill_blank` type. Reading
       * `blanks` here answered every fill-in-the-blanks question with an em dash,
       * which reads as "there is no right answer" on a learner's report and leaves a
       * teacher with nothing to re-teach from. `blanks` is still read second, so a
       * block written the other way round keeps working.
       */
      case 'fill_blanks':
        return (
          slide.fillBlanksAnswers?.map(a => a.answer).join(', ') ||
          slide.blanks?.map((b: FillBlank) => b.answer).join(', ') ||
          '—'
        )
      case 'mark_words':
        return slide.markWordsCorrect?.join(', ') || '—'
      case 'complex_fill_blank':
        return slide.complexBlanks?.map(b => b.answer).join(', ') || '—'
      case 'find_words':
        return slide.findWordsTargets?.join(', ') || '—'
      case 'matching':
        return slide.pairs?.map(p => `${p.left} → ${p.right}`).join('; ') || '—'
      default:
        return '—'
    }
  } catch {
    return '—'
  }
}
